import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, MapPin, Clock, Home, Wrench, Lightbulb, Handshake } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import OrganizationSchema from '@/components/schema/OrganizationSchema';

const About = () => {
  const values = [
    {
      icon: <Award className="w-8 h-8 text-green-600" />,
      title: 'Professional Excellence',
      description: 'Fully accredited assessors delivering high-quality energy assessments to industry standards.'
    },
    {
      icon: <Lightbulb className="w-8 h-8 text-cyan-500" />,
      title: 'Practical Experience',
      description: 'Real-world retrofit experience ensuring advice that works in practice, not just theory.'
    },
    {
      icon: <MapPin className="w-8 h-8 text-green-600" />,
      title: 'West Country Coverage',
      description: 'Serving Exeter, Devon, Cornwall, Somerset and the wider South West since 2009.'
    },
    {
      icon: <Handshake className="w-8 h-8 text-cyan-500" />,
      title: 'Trusted Network',
      description: 'Working with a reliable network of assessors, installers and tradespeople.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="About Energy Performance Direct | Accredited Energy Assessors Exeter"
        description="Energy Performance Direct provides professional EPCs, energy advice, and building compliance services across Exeter, Devon, and the South West. Fully accredited by Elmhurst, TrustMark, and Quidos."
        keywords="about us, energy assessors Exeter, EPC company Devon, accredited energy assessors, TrustMark EPC"
        canonical="/about"
      />
      <OrganizationSchema />
      <Breadcrumbs items={[
        { name: 'About', path: '/about' }
      ]} />
      <Navbar />

      {/* Header */}
      <section className="py-16" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 text-white" data-testid="about-heading">
              About Us
            </h1>
            <p className="text-base sm:text-lg opacity-95 text-white">
              Professional energy assessment services since 2009
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6">
                Energy Performance Direct was founded in 2009 and is led by Managing Director <Link to="/author/zak-henning" className="text-green-700 underline">Zak Henning</Link>, a qualified Domestic and Non-Domestic Energy Assessor with extensive experience working across the West Country. Over the years, we've assessed and advised on a wide variety of buildings — from large hotels and commercial premises to small warehouses and family homes — helping clients improve energy performance, meet regulatory requirements, and make informed decisions about their buildings.
              </p>
              <p className="text-base text-gray-700 mb-6">
                Our work covers EPCs, RdSAP and SBEM calculations, SAP compliance advice, renewable energy guidance, and the preparation of floor plans and lease plans. We were also closely involved in the Green Deal, supporting many property owners through energy efficiency upgrades during its operation.
              </p>
              <p className="text-base text-gray-700 mb-6">
                Over time our practice has grown into what we now describe as an <strong>independent building energy consultancy</strong>. Alongside the regulated EPC and SBEM lodgement work, we produce structured advice reports — <strong>DEAR</strong> (Domestic) and <strong>NDEAR</strong> (Non-Domestic) — using a proprietary <Link to="/methodology" className="text-green-700 underline">League Table &amp; Rafts methodology</Link>. Every measure is modelled in isolation, ranked across five criteria, then grouped into three costed implementation pathways so clients get an evidence-based roadmap rather than a static recommendation list.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Retrofit Experience */}
      <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                  <Home className="w-6 h-6 text-white" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Hands-On Retrofit Experience</h2>
                <p className="text-base text-gray-700 mb-4">
                  More recently, Zak qualified as a Retrofit Assessor and Retrofit Coordinator, developing a detailed understanding of the PAS 2035 route to retrofit. That knowledge isn't just theoretical. A full, whole-house retrofit of his own home — improving it from a D-rated to an A-rated building — provided invaluable hands-on experience.
                </p>
                <p className="text-base text-gray-700 mb-4">
                  The project included improved insulation, new windows, solar PV, a heat pump, upgraded plumbing and electrics, careful management of thermal bridges, and mitigation of condensation risk, all delivered within a realistic household budget.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                  <Lightbulb className="w-6 h-6 text-white" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Practical, Achievable Advice</h2>
                <p className="text-base text-gray-700 mb-4">
                  That practical experience shapes how we work. After years of advising clients through EPCs and energy calculations for both commercial and domestic buildings, it reinforces one simple truth: good energy advice has to work in the real world.
                </p>
                <p className="text-base text-gray-700">
                  Whether the aim is to reduce energy bills, improve an EPC rating, lower carbon emissions, or comply with regulations such as MEES, our focus is on clear, achievable improvements rather than theoretical perfection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Network */}
      <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                  <Handshake className="w-6 h-6 text-white" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Trusted Partner Network</h2>
                <p className="text-base text-gray-700">
                  Energy Performance Direct works closely with a trusted network of partners across the energy and construction sectors. We collaborate with local accredited assessors, retrofit professionals, and installers of solar PV, heat pumps and biomass systems, alongside experienced traditional trades including plumbers, electricians, builders and plasterers. This joined-up approach helps ensure clients receive well-coordinated advice that can actually be delivered.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-100 to-cyan-100 rounded-full mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{value.title}</h3>
                  <p className="text-sm text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditations */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Fully Accredited</h2>
            <p className="text-gray-700 mb-8">
              We are proud to be accredited by leading industry bodies, ensuring our assessments meet the highest standards.
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/ejoqsqlu_image.png" 
                alt="Elmhurst Energy" 
                className="h-16"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/gfybwp7x_image.png" 
                alt="TrustMark" 
                className="h-16"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/b08gspqy_image.png" 
                alt="Buy With Confidence" 
                className="h-16"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;